import React, { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@iconify/react";
import Logo from "../Logo";
import s from "./Auth.module.css";

// > 0 while a shell is on screen: login <-> register swaps skip the intro
let mountedShells = 0;

/** Autofocus only where it won't pop up the on-screen keyboard. */
export const canAutoFocus = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

/**
 * Shared frame for the login and register modals.
 * Rendered in a portal so it sits above the sticky header and mobile dock.
 *
 * onClose   - the X button
 * onDismiss - backdrop click / Esc (omit to ignore them, e.g. half-filled form)
 */
export default function AuthShell({
  mode,
  onSwitchMode,
  onClose,
  onDismiss,
  hideClose,
  aside,
  children,
}) {
  const [isSwap] = useState(() => mountedShells > 0);
  const titleId = useId();

  useEffect(() => {
    mountedShells += 1;
    document.body.style.overflow = "hidden";
    return () => {
      mountedShells -= 1;
      document.body.style.removeProperty("overflow");
    };
  }, []);

  useEffect(() => {
    if (!onDismiss) return undefined;
    const onKey = (e) => e.key === "Escape" && onDismiss();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onDismiss]);

  const isLogin = mode === "login";

  return createPortal(
    <div
      className={`${s.overlay} ${isSwap ? s.noIntro : ""}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && onDismiss) onDismiss();
      }}
    >
      <div
        className={s.card}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {!hideClose && (
          <button type="button" className={s.close} onClick={onClose} aria-label="ปิด">
            <Icon icon="solar:close-circle-linear" width={26} />
          </button>
        )}

        <aside className={`${s.aside} ${isLogin ? s.asideScrim : ""}`}>
          <img
            src="/lux/hero/girl-gold.webp"
            alt=""
            aria-hidden="true"
            className={`${s.asideArt} ${isLogin ? "" : s.asideArtDim}`}
          />
          <div className={s.asideLogo}>
            <Logo />
          </div>
          <div className={`${s.asideBody} ${isLogin ? s.asideBodyEnd : ""}`}>
            {aside}
          </div>
        </aside>

        <div className={s.main}>
          <div className={s.mobileLogo}>
            <Logo />
          </div>

          <h2 id={titleId} className={s.srOnly}>
            {isLogin ? "เข้าสู่ระบบ" : "สมัครสมาชิก"}
          </h2>

          <div className={s.tabs} data-mode={mode} data-swap={isSwap || undefined}>
            <span className={s.tabPill} aria-hidden="true" />
            <button
              type="button"
              className={`${s.tab} ${isLogin ? s.tabOn : ""}`}
              aria-pressed={isLogin}
              onClick={isLogin ? undefined : onSwitchMode}
            >
              เข้าสู่ระบบ
            </button>
            <button
              type="button"
              className={`${s.tab} ${!isLogin ? s.tabOn : ""}`}
              aria-pressed={!isLogin}
              onClick={isLogin ? onSwitchMode : undefined}
            >
              สมัครสมาชิก
            </button>
          </div>

          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
